
import React, { useState, useCallback, useEffect } from 'react';
import { MainScreen } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useVault } from '../contexts/VaultContext';
import { useData } from '../contexts/DataContext';
import { useTheme } from '../contexts/ThemeContext';
import Dashboard from './Dashboard';
import Transactions from './Transactions';
import Cards from './Cards';
import Reports from './Reports';
import Profile from './Profile';
import Wealth from './Wealth';
import Navigation from './Navigation';
import MasterKeySetup from './MasterKeySetup';
import VaultRecovery from './VaultRecovery';
import ConfirmDialog from './ConfirmDialog';
import MasterKeyVerifyDialog from './MasterKeyVerifyDialog';
import LandingPage from './LandingPage';
import Logo from './Logo';
import { sheetsService } from '../utils/googleSheets';
import { deriveKeyFromPassword, unwrapKey, exportKeyToSession } from '../utils/crypto';
import { LEGAL_CONTENT } from '../legalContent';

const SESSION_VAULT_NAME = '_eq_vlt_blob';

const SCREEN_PATHS: Record<MainScreen, string> = {
  [MainScreen.DASHBOARD]: '/inicio',
  [MainScreen.TRANSACTIONS]: '/transacoes',
  [MainScreen.CARDS]: '/cartoes',
  [MainScreen.WEALTH]: '/patrimonio',
  [MainScreen.REPORTS]: '/analises',
  [MainScreen.PROFILE]: '/ajustes',
};

const PATH_TO_SCREEN: Record<string, MainScreen> = Object.entries(SCREEN_PATHS).reduce((acc, [key, val]) => {
  acc[val] = Number(key);
  return acc;
}, {} as any);

const AppRouter: React.FC = () => {
  const { 
    isAuthenticated, 
    isBlocked,
    isExpired,
    user, 
    userStatus, 
    isLoading, 
    loadingMessage, 
    spreadsheetId,
    login,
    logout,
    initAuth,
    setLoading
  } = useAuth();

  const { isVaultLocked, unlockVault, isUnlocking, unlockError } = useVault();
  const { 
    transactions, 
    cards, 
    wealthItems, 
    addTransaction, 
    deleteTransaction, 
    updateTransaction,
    addCard,
    updateCard,
    deleteCard,
    saveWealthItem,
    deleteWealthItem,
    clearTransactions,
    repairData,
    loadAllData
  } = useData();

  const { theme, toggleTheme } = useTheme();

  const [activeScreen, setActiveScreenState] = useState<MainScreen>(() => {
    const path = window.location.pathname;
    return PATH_TO_SCREEN[path] ?? MainScreen.DASHBOARD;
  });

  const [showSetup, setShowSetup] = useState<boolean>(false);
  const [showRecovery, setShowRecovery] = useState<boolean>(false);
  const [unlockKey, setUnlockKey] = useState<string>('');
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(() => {
    return localStorage.getItem('equilibra_terms_accepted') === 'true';
  });

  const handleSetAcceptedTerms = useCallback((val: boolean) => {
    setAcceptedTerms(val);
    if (val) {
      localStorage.setItem('equilibra_terms_accepted', 'true');
    } else {
      localStorage.removeItem('equilibra_terms_accepted');
    }
  }, []);
  
  const [notification, setNotification] = useState<{isOpen: boolean, title: string, message: string, type: 'success' | 'danger' | 'info' | 'warning', isAlert?: boolean, icon?: string}>({
    isOpen: false, title: '', message: '', type: 'info', isAlert: true, icon: undefined
  });

  const [verifyAction, setVerifyAction] = useState<{ 
    isOpen: boolean; onVerified: () => void; title: string; message: string; icon?: string;
  }>({ isOpen: false, onVerified: () => {}, title: '', message: '', icon: undefined });

  const handleTriggerVerify = useCallback((title: string, message: string, onVerified: () => void, icon?: string) => {
    setVerifyAction({ isOpen: true, title, message, onVerified, icon });
  }, []);

  const setActiveScreen = useCallback((screen: MainScreen) => {
    setActiveScreenState(screen);
    const newPath = SCREEN_PATHS[screen];
    if (window.location.pathname !== newPath) {
      window.history.pushState({ screen }, '', newPath);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const screen = PATH_TO_SCREEN[path] ?? MainScreen.DASHBOARD;
      setActiveScreenState(screen);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Redirect root to dashboard if authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading && window.location.pathname === '/') {
      setActiveScreen(MainScreen.DASHBOARD);
    }
  }, [isAuthenticated, isLoading, setActiveScreen]);

  const isLegalPath = window.location.pathname === '/privacidade' || window.location.pathname === '/termos';

  if (isLegalPath) {
    const type = window.location.pathname === '/termos' ? 'terms' : 'privacy';
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-12 flex flex-col items-center">
        <div className="w-full max-w-4xl bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="p-8 md:p-12">
            <div className="flex items-center justify-between mb-12">
               <div className="flex items-center gap-4">
                  <Logo size="lg" />
                  <h1 className="text-2xl font-black uppercase tracking-tight text-slate-900 dark:text-white">
                    {type === 'terms' ? 'Termos de Uso' : 'Política de Privacidade'}
                  </h1>
               </div>
               <a href="/" className="text-[10px] font-black uppercase tracking-widest text-blue-600 hover:underline">Voltar</a>
            </div>
            <div className="space-y-8">
               {LEGAL_CONTENT[type].text}
            </div>
          </div>
          <div className="p-8 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 text-center">
             <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Equilibra Finance • Soberania de Dados</p>
          </div>
        </div>
      </div>
    );
  }

  const handleClearTransactionsWrapper = useCallback(async (start: Date, end: Date) => {
    try {
      await clearTransactions(start, end);
      setNotification({
        isOpen: true, title: 'Limpeza Concluída',
        message: 'Os registros do período foram removidos com sucesso.',
        type: 'success', isAlert: true
      });
    } catch (e) {
      alert("Erro ao sincronizar limpeza.");
    }
  }, [clearTransactions]);

  const handleRepairDataWrapper = useCallback(async () => {
    try {
      await repairData();
      setNotification({
        isOpen: true, title: 'Reparo Concluído',
        message: 'A estrutura da sua planilha foi reorganizada e os dados foram sincronizados com sucesso.',
        type: 'success', isAlert: true, icon: 'fa-wrench'
      });
    } catch (e) {
      setNotification({
        isOpen: true, title: 'Erro no Reparo',
        message: 'Não foi possível completar o reparo da planilha. Tente novamente mais tarde.',
        type: 'danger', isAlert: true, icon: 'fa-triangle-exclamation'
      });
    }
  }, [repairData]);

  if (isLoading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="mb-6 animate-bounce">
          <Logo size="xl" />
      </div>
      <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 dark:text-slate-400">{loadingMessage}</p>
    </div>
  );

  if (!isAuthenticated) return (
    <LandingPage 
      acceptedTerms={acceptedTerms} 
      setAcceptedTerms={handleSetAcceptedTerms} 
      theme={theme}
      toggleTheme={toggleTheme}
    />
  );

  if (isVaultLocked) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 p-8 text-center">
      <div className="w-full max-w-[360px] animate-in zoom-in duration-500">
        <div className="w-16 h-16 bg-blue-600/10 text-blue-500 rounded-3xl flex items-center justify-center text-2xl mb-10 mx-auto border border-blue-500/20"><i className="fa-solid fa-lock"></i></div>
        <h2 className="text-2xl font-black text-white mb-8 uppercase tracking-tight">Cofre Ativo</h2>
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (!unlockKey) return;
          const success = await unlockVault(unlockKey);
          if (success) {
            setUnlockKey('');
          }
        }} className="space-y-4">
          <input required type="password" value={unlockKey} onChange={e => setUnlockKey(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-2xl px-6 py-5 text-sm font-bold text-white outline-none focus:ring-2 focus:ring-blue-500 text-center" placeholder="Sua Chave Mestra" />
          {unlockError && <p className="text-red-500 text-[10px] font-black uppercase">{unlockError}</p>}
          <button type="submit" disabled={isUnlocking} className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl active:scale-95 transition-all">{isUnlocking ? "Validando..." : "Desbloquear"}</button>
        </form>
        <button onClick={() => setShowRecovery(true)} className="mt-10 text-blue-500 text-[10px] font-black uppercase tracking-widest">Esqueci minha chave</button>
      </div>
      {showRecovery && <VaultRecovery user={user!} onComplete={async (k, s, wr, wp) => {
        setLoading(true, 'Atualizando segurança...');
        try {
          await sheetsService.updateSecurityMetadata({ salt: s, encryption_verify: wp, wrapped_key: wr });
          await initAuth();
          setShowRecovery(false);
        } catch (e) {
          alert("Erro ao recuperar.");
        } finally {
          setLoading(false);
        }
      }} onClose={() => setShowRecovery(false)} />}
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen md:pl-64 bg-slate-100 dark:bg-slate-950">
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 md:px-12 pt-0 md:pt-12 pb-24 md:pb-12">
        <div className="animate-in fade-in duration-700">
          {((isExpired || isBlocked) && activeScreen !== MainScreen.PROFILE && activeScreen !== MainScreen.REPORTS) ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
              <div className={`w-24 h-24 ${isBlocked ? 'bg-red-100 dark:bg-red-900/20 text-red-600 shadow-red-500/20' : 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 shadow-amber-500/20'} rounded-[2rem] flex items-center justify-center text-4xl mb-10 shadow-2xl animate-in zoom-in duration-500`}>
                <i className={`fa-solid ${isBlocked ? 'fa-user-slash' : 'fa-hourglass-end'}`}></i>
              </div>
              <h2 className="text-3xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-6">
                {isBlocked ? 'Acesso Bloqueado' : 'Acesso Expirado'}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-sm mb-12 font-medium leading-relaxed">
                {isBlocked 
                  ? (userStatus === 'chargeback' 
                      ? 'Seu acesso foi interrompido devido a um estorno de pagamento. Se você acredita que isso é um erro, entre em contato com o suporte.'
                      : 'Sua conta foi suspensa por um administrador. Você ainda pode exportar seus dados ou verificar seu plano nos ajustes.')
                  : 'Sua assinatura chegou ao fim. Renove seu plano para continuar acessando seus dados e mantendo sua soberania financeira.'}
              </p>
              <div className="flex flex-col gap-4 w-full max-w-xs mx-auto">
                <button 
                  onClick={() => {
                    if (isBlocked && userStatus === 'chargeback') {
                      const rawUrl = import.meta.env.VITE_TELEGRAM_SUPPORT_URL || 'https://t.me/equilibrafinance';
                      const telegram = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
                      window.open(telegram, '_blank');
                    } else {
                      setActiveScreen(MainScreen.PROFILE);
                    }
                  }}
                  className="px-12 py-5 bg-blue-600 text-white rounded-2xl font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-blue-500/30 active:scale-95 transition-all w-full transform hover:-translate-y-1"
                >
                  {isBlocked ? (userStatus === 'chargeback' ? 'Falar com Suporte' : 'Verificar Plano') : 'Renovar Agora'}
                </button>
                <button 
                  onClick={() => setActiveScreen(MainScreen.REPORTS)}
                  className="px-12 py-5 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-800 rounded-2xl font-black text-[11px] uppercase tracking-widest active:scale-95 transition-all w-full"
                >
                  Exportar Meus Dados
                </button>
              </div>
            </div>
          ) : (
            <>
              {activeScreen === MainScreen.DASHBOARD && (
            <Dashboard 
              transactions={transactions} 
              cards={cards} 
              onAddTransaction={addTransaction} 
              theme={theme} 
              toggleTheme={toggleTheme} 
              needsSetup={!user?.salt} 
              onTriggerSetup={() => setShowSetup(true)} 
              user={user} 
              userStatus={userStatus}
            />
          )}
          {activeScreen === MainScreen.TRANSACTIONS && (
            <Transactions 
              transactions={transactions} 
              onAddTransaction={addTransaction} 
              onDeleteTransaction={id => { 
                handleTriggerVerify('Excluir Lançamento', 'Deseja apagar permanentemente este registro?', () => deleteTransaction(id), 'fa-trash-can'); 
              }} 
              onUpdateTransaction={updateTransaction} 
              onTriggerVerify={handleTriggerVerify} 
              cards={cards} 
              needsSetup={!user?.salt} 
              onTriggerSetup={() => setShowSetup(true)} 
            />
          )}
          {activeScreen === MainScreen.CARDS && (
            <Cards 
              cards={cards} 
              onAddCard={addCard} 
              onUpdateCard={updateCard} 
              onDeleteCard={id => { 
                handleTriggerVerify('Remover Cartão', 'Todos os dados vinculados a este cartão serão protegidos.', () => deleteCard(id), 'fa-credit-card'); 
              }} 
              onTriggerVerify={handleTriggerVerify} 
              transactions={transactions} 
              onAddTransaction={addTransaction} 
              needsSetup={!user?.salt} 
              onTriggerSetup={() => setShowSetup(true)} 
            />
          )}
          {activeScreen === MainScreen.WEALTH && (
            <Wealth 
              items={wealthItems} 
              onSave={saveWealthItem} 
              onDelete={id => { 
                handleTriggerVerify('Remover Ativo', 'Esta ação é irreversível.', () => deleteWealthItem(id), 'fa-vault'); 
              }} 
              onTriggerVerify={handleTriggerVerify} 
              transactions={transactions} 
              onAddTransaction={t => { 
                addTransaction(t); 
              }} 
              needsSetup={!user?.salt} 
              onTriggerSetup={() => setShowSetup(true)} 
            />
          )}
          {activeScreen === MainScreen.REPORTS && <Reports transactions={transactions} cards={cards} onTriggerVerify={handleTriggerVerify} />}
          {activeScreen === MainScreen.PROFILE && (
              <Profile 
                user={user} 
                transactions={transactions} 
                onLogout={logout} 
                onRefreshData={loadAllData} 
                theme={theme} 
                toggleTheme={toggleTheme} 
                onSyncFromSheet={loadAllData} 
                onClearTransactions={handleClearTransactionsWrapper} 
                onRepairData={handleRepairDataWrapper}
                onTriggerVerify={handleTriggerVerify} 
                subscriptionStatus={userStatus} 
              />
          )}
        </>
      )}
    </div>
  </main>
      
      {showSetup && <MasterKeySetup onComplete={async (k, s, wr, wp) => {
        setLoading(true, 'Salvando Chave Mestra...');
        try {
            await sheetsService.updateSecurityMetadata({ salt: s, encryption_verify: wp, wrapped_key: wr });
            await initAuth(); 
            setShowSetup(false);
        } catch (e) { alert("Erro ao salvar."); }
        finally { setLoading(false); }
      }} onClose={() => setShowSetup(false)} />}
      
      <ConfirmDialog isOpen={notification.isOpen} onClose={() => setNotification(p => ({...p, isOpen: false}))} onConfirm={() => {}} title={notification.title} message={notification.message} type={notification.type} isAlert={notification.isAlert} icon={notification.icon} />
      <MasterKeyVerifyDialog isOpen={verifyAction.isOpen} onClose={() => setVerifyAction(p => ({ ...p, isOpen: false }))} onVerified={verifyAction.onVerified} user={user} title={verifyAction.title} message={verifyAction.message} icon={verifyAction.icon} />
      
      <Navigation activeScreen={activeScreen} setActiveScreen={setActiveScreen} theme={theme} toggleTheme={toggleTheme} />
    </div>
  );
};

export default AppRouter;
